import React, { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../ui/sheet';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { History, RotateCcw, Lock, Loader2, Clock, GitCompare, Eye } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation, Trans } from "react-i18next";
import { getArticleRevisions, getArticleRevision } from '../../../services/api';
import type { ArticleRevisionMeta, ArticleRevisionFull } from '@/types';
import UpsellModal from '../../modals/UpsellModal';
import { VersionDiffDialog } from './VersionDiffDialog';
import { VersionPreviewDialog } from './VersionPreviewDialog';

interface ArticleVersionHistoryProps {
  helpCenterId: string;
  itemId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user restores a version — load it into the editor as unsaved changes. */
  onRestore: (revision: ArticleRevisionFull) => void;
  /** Current (in-editor) content, used as the "Current version" side of a comparison. */
  currentTitle: string;
  currentDescription: string;
  currentHtml: string;
}

export const ArticleVersionHistory: React.FC<ArticleVersionHistoryProps> = ({
  helpCenterId,
  itemId,
  open,
  onOpenChange,
  onRestore,
  currentTitle,
  currentDescription,
  currentHtml,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [revisions, setRevisions] = useState<ArticleRevisionMeta[]>([]);
  const [lockedCount, setLockedCount] = useState(0);
  const [isPaid, setIsPaid] = useState<boolean | null>(null);
  const [limit, setLimit] = useState(2);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [showUpsell, setShowUpsell] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffFromId, setDiffFromId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getArticleRevisions(helpCenterId, itemId);
        if (cancelled) return;
        if (res.ok) {
          setRevisions(res.revisions || []);
          setLockedCount(res.lockedCount || 0);
          setIsPaid(res.isPaid ?? null);
          setLimit(res.limit ?? 2);
        } else {
          toast.error(res.error || t('helpCenter.articleVersionHistory.loadHistoryFailed'));
        }
      } catch {
        if (!cancelled) toast.error(t('helpCenter.articleVersionHistory.loadHistoryFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, helpCenterId, itemId]);

  const handleRestore = async (id: string) => {
    setRestoringId(id);
    try {
      const res = await getArticleRevision(helpCenterId, itemId, id);
      if (res.ok && res.revision) {
        onRestore(res.revision);
        onOpenChange(false);
        toast.success(t('helpCenter.articleVersionHistory.versionLoaded'));
      } else {
        toast.error(res.error || t('helpCenter.articleVersionHistory.loadVersionFailed'));
      }
    } catch (e: any) {
      if (e?.response?.status === 403) {
        setShowUpsell(true);
      } else {
        toast.error(t('helpCenter.articleVersionHistory.loadVersionFailed'));
      }
    } finally {
      setRestoringId(null);
    }
  };

  const fmt = (s: string) => {
    try {
      return new Date(s).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return s;
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <History className="h-4 w-4" />
              {t('helpCenter.articleVersionHistory.title')}
            </SheetTitle>
            <SheetDescription>
              {t('helpCenter.articleVersionHistory.description')}
            </SheetDescription>
          </SheetHeader>

          {!loading && isPaid === false && (
            <div className="mt-4 flex items-center justify-between gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              <span>{t('helpCenter.articleVersionHistory.freePlanLimit', { limit })}</span>
              <button
                type="button"
                onClick={() => setShowUpsell(true)}
                className="shrink-0 font-medium text-primary hover:underline"
              >
                {t('helpCenter.articleVersionHistory.upgradeToKeep10')}
              </button>
            </div>
          )}

          <div className="mt-4 space-y-2">
            {loading ? (
              <>
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </>
            ) : revisions.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                <Trans i18nKey="helpCenter.articleVersionHistory.noVersions" components={{ b: <b /> }}>
                  No previous versions yet. A version is saved automatically each time you edit and <b>Publish</b> this article.
                </Trans>
              </div>
            ) : (
              revisions.map((rev) => (
                <div key={rev.id} className="rounded-md border p-3">
                  <div className="flex items-center gap-1.5 text-sm">
                    <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span>{fmt(rev.created_at)}</span>
                  </div>
                  {rev.author && (
                    <p className="mt-0.5 text-xs text-muted-foreground">{t('helpCenter.articleVersionHistory.byAuthor', { author: rev.author })}</p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1.5 text-muted-foreground"
                      onClick={() => {
                        setPreviewId(rev.id);
                        setPreviewOpen(true);
                      }}
                      title={t('helpCenter.articleVersionHistory.previewTooltip')}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      {t('helpCenter.articleVersionHistory.preview')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1.5 text-muted-foreground"
                      onClick={() => {
                        setDiffFromId(rev.id);
                        setDiffOpen(true);
                      }}
                      title={t('helpCenter.articleVersionHistory.compareTooltip')}
                    >
                      <GitCompare className="h-3.5 w-3.5" />
                      {t('helpCenter.articleVersionHistory.compare')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => handleRestore(rev.id)}
                      disabled={restoringId !== null}
                    >
                      {restoringId === rev.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RotateCcw className="h-3.5 w-3.5" />
                      )}
                      {t('helpCenter.articleVersionHistory.restore')}
                    </Button>
                  </div>
                </div>
              ))
            )}

            {!loading && lockedCount > 0 && (
              <button
                type="button"
                onClick={() => setShowUpsell(true)}
                className="flex w-full items-center justify-between gap-3 rounded-md border border-dashed p-3 text-left transition-colors hover:bg-muted/50"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Lock className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    {t('helpCenter.articleVersionHistory.olderVersions', { count: lockedCount })}
                  </span>
                </div>
                <span className="shrink-0 text-xs font-medium text-primary">
                  {t('helpCenter.articleVersionHistory.upgradeToAccess')}
                </span>
              </button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <UpsellModal isOpen={showUpsell} onClose={() => setShowUpsell(false)} />

      <VersionDiffDialog
        helpCenterId={helpCenterId}
        itemId={itemId}
        revisions={revisions}
        currentTitle={currentTitle}
        currentDescription={currentDescription}
        currentHtml={currentHtml}
        initialFromId={diffFromId}
        open={diffOpen}
        onOpenChange={setDiffOpen}
      />

      <VersionPreviewDialog
        helpCenterId={helpCenterId}
        itemId={itemId}
        revisionId={previewId}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
      />
    </>
  );
};

export default ArticleVersionHistory;
